import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider } from "@/context/AuthContext";
import { CartProvider } from "@/context/CartContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import PublicLayout from "@/components/PublicLayout";

import Home from "@/pages/Home";
import Courses from "@/pages/Courses";
import CourseDetail from "@/pages/CourseDetail";
import Store from "@/pages/Store";
import ProductDetail from "@/pages/ProductDetail";
import Cart from "@/pages/Cart";
import Checkout from "@/pages/Checkout";
import Tests from "@/pages/Tests";
import VerifyCertificate from "@/pages/VerifyCertificate";
import CmsPage from "@/pages/CmsPage";
import ScrollToTop from "@/components/ScrollToTop";
import FranchiseLanding from "@/pages/franchise/FranchiseLanding";
import FranchiseApply from "@/pages/franchise/FranchiseApply";
import InstituteLayout from "@/pages/institute/InstituteLayout";
import InstituteDashboard from "@/pages/institute/InstituteDashboard";
import InstituteStudents from "@/pages/institute/InstituteStudents";
import InstituteCommissions from "@/pages/institute/InstituteCommissions";
import InstituteBank from "@/pages/institute/InstituteBank";
import AdminFranchise from "@/pages/admin/AdminFranchise";
import AdminEducatorPlans from "@/pages/admin/AdminEducatorPlans";
import AdminInternships from "@/pages/admin/AdminInternships";
import InternshipLayout from "@/pages/internship/InternshipLayout";
import InternshipLanding from "@/pages/internship/InternshipLanding";
import InternshipDetail from "@/pages/internship/InternshipDetail";
import InternshipApply from "@/pages/internship/InternshipApply";
import VerifyInternshipCertificate from "@/pages/internship/VerifyInternshipCertificate";
import MyInternship from "@/pages/learner/MyInternship";
import PackDetail from "@/pages/testseries/PackDetail";
import ExamAttempt from "@/pages/testseries/ExamAttempt";
import AttemptResult from "@/pages/testseries/AttemptResult";
import MyTestSeries from "@/pages/learner/MyTestSeries";
import AdminTestSeries from "@/pages/admin/AdminTestSeries";

import Login from "@/pages/auth/Login";
import AdminLogin from "@/pages/auth/AdminLogin";
import Register from "@/pages/auth/Register";
import Forgot from "@/pages/auth/Forgot";
import AuthCallback from "@/pages/auth/AuthCallback";

import LearnerLayout from "@/pages/learner/LearnerLayout";
import LearnerDashboard from "@/pages/learner/LearnerDashboard";
import MyCourses from "@/pages/learner/MyCourses";
import CoursePlayer from "@/pages/learner/CoursePlayer";
import LearnerTests from "@/pages/learner/LearnerTests";
import TestAttempt from "@/pages/learner/TestAttempt";
import Results from "@/pages/learner/Results";
import LiveClasses from "@/pages/learner/LiveClasses";
import Orders from "@/pages/learner/Orders";
import Profile from "@/pages/learner/Profile";

import EducatorLayout from "@/pages/educator/EducatorLayout";
import EducatorDashboard from "@/pages/educator/EducatorDashboard";
import CourseBuilder from "@/pages/educator/CourseBuilder";
import EducatorLive from "@/pages/educator/EducatorLive";
import EducatorTests from "@/pages/educator/EducatorTests";
import EducatorSubscription, { PlanGate } from "@/pages/educator/EducatorSubscription";

import SellerLayout from "@/pages/seller/SellerLayout";
import SellerDashboard from "@/pages/seller/SellerDashboard";
import SellerWallet from "@/pages/seller/SellerWallet";

import AdminLayout from "@/pages/admin/AdminLayout";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminUsers from "@/pages/admin/AdminUsers";
import AdminCourses from "@/pages/admin/AdminCourses";
import AdminCategories from "@/pages/admin/AdminCategories";
import AdminProducts from "@/pages/admin/AdminProducts";
import AdminOrders from "@/pages/admin/AdminOrders";
import AdminCommissions from "@/pages/admin/AdminCommissions";
import AdminWithdrawals from "@/pages/admin/AdminWithdrawals";
import AdminBanners from "@/pages/admin/AdminBanners";
import AdminReviews from "@/pages/admin/AdminReviews";
import AdminLeads from "@/pages/admin/AdminLeads";
import AdminCms from "@/pages/admin/AdminCms";
import AdminLogs from "@/pages/admin/AdminLogs";
import AdminRoles from "@/pages/admin/AdminRoles";
import AdminSettings from "@/pages/admin/AdminSettings";

const ADMIN_ROLES = ["super_admin", "admin", "sub_admin"];

function App() {
  return (
    <AuthProvider>
      <CartProvider>
        <Toaster position="top-right" richColors />
        <BrowserRouter>
          <ScrollToTop />
          <Routes>
            {/* Public */}
            <Route element={<PublicLayout />}>
              <Route path="/" element={<Home />} />
              <Route path="/courses" element={<Courses />} />
              <Route path="/courses/:id" element={<CourseDetail />} />
              <Route path="/store" element={<Store />} />
              <Route path="/store/:id" element={<ProductDetail />} />
              <Route path="/cart" element={<Cart />} />
              <Route path="/checkout" element={<Checkout />} />
              <Route path="/tests" element={<Tests />} />
              <Route path="/tests/:slug" element={<PackDetail />} />
              <Route path="/verify" element={<VerifyCertificate />} />
              <Route path="/verify/:certNo" element={<VerifyCertificate />} />
              <Route path="/page/:slug" element={<CmsPage />} />
              <Route path="/institute-franchise" element={<FranchiseLanding />} />
              <Route path="/institute-franchise/apply" element={<FranchiseApply />} />
            </Route>

            {/* Internship program */}
            <Route element={<InternshipLayout />}>
              <Route path="/internship" element={<InternshipLanding />} />
              <Route path="/internship/apply" element={<InternshipApply />} />
              <Route path="/internship/:slug" element={<InternshipDetail />} />
              <Route path="/verify-certificate" element={<VerifyInternshipCertificate />} />
              <Route path="/verify-certificate/:certNo" element={<VerifyInternshipCertificate />} />
            </Route>

            {/* Institute partner */}
            <Route path="/institute" element={<ProtectedRoute roles={["institute"]}><InstituteLayout /></ProtectedRoute>}>
              <Route index element={<InstituteDashboard />} />
              <Route path="students" element={<InstituteStudents />} />
              <Route path="commissions" element={<InstituteCommissions />} />
              <Route path="bank" element={<InstituteBank />} />
            </Route>

            {/* Auth */}
            <Route path="/login" element={<Login />} />
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/register" element={<Register />} />
            <Route path="/forgot-password" element={<Forgot />} />
            <Route path="/auth/callback" element={<AuthCallback />} />

            {/* Learner */}
            <Route path="/learner" element={<ProtectedRoute><LearnerLayout /></ProtectedRoute>}>
              <Route index element={<LearnerDashboard />} />
              <Route path="my-courses" element={<MyCourses />} />
              <Route path="tests" element={<LearnerTests />} />
              <Route path="results" element={<Results />} />
              <Route path="live" element={<LiveClasses />} />
              <Route path="orders" element={<Orders />} />
              <Route path="internship" element={<MyInternship />} />
              <Route path="test-series" element={<MyTestSeries />} />
              <Route path="test-series/result/:aid" element={<AttemptResult />} />
              <Route path="profile" element={<Profile />} />
            </Route>
            <Route path="/learner/learn/:id" element={<ProtectedRoute><CoursePlayer /></ProtectedRoute>} />
            <Route path="/learner/test/:id" element={<ProtectedRoute><TestAttempt /></ProtectedRoute>} />
            <Route path="/test-series/attempt/:testId" element={<ProtectedRoute><ExamAttempt /></ProtectedRoute>} />

            {/* Educator */}
            <Route path="/educator" element={<ProtectedRoute roles={["educator", "super_admin", "admin"]}><EducatorLayout /></ProtectedRoute>}>
              <Route index element={<PlanGate><EducatorDashboard /></PlanGate>} />
              <Route path="courses" element={<PlanGate><CourseBuilder /></PlanGate>} />
              <Route path="live" element={<PlanGate><EducatorLive /></PlanGate>} />
              <Route path="tests" element={<PlanGate><EducatorTests /></PlanGate>} />
              <Route path="subscription" element={<EducatorSubscription />} />
              <Route path="earnings" element={<InstituteCommissions />} />
              <Route path="bank" element={<InstituteBank />} />
            </Route>

            {/* Seller */}
            <Route path="/seller" element={<ProtectedRoute roles={["content_seller", "super_admin", "admin"]}><SellerLayout /></ProtectedRoute>}>
              <Route index element={<SellerDashboard />} />
              <Route path="wallet" element={<SellerWallet />} />
            </Route>

            {/* Admin */}
            <Route path="/admin" element={<ProtectedRoute roles={ADMIN_ROLES}><AdminLayout /></ProtectedRoute>}>
              <Route index element={<AdminDashboard />} />
              <Route path="users" element={<AdminUsers />} />
              <Route path="courses" element={<AdminCourses />} />
              <Route path="categories" element={<AdminCategories />} />
              <Route path="products" element={<AdminProducts />} />
              <Route path="manage-courses" element={<CourseBuilder />} />
              <Route path="orders" element={<AdminOrders />} />
              <Route path="commissions" element={<AdminCommissions />} />
              <Route path="withdrawals" element={<AdminWithdrawals />} />
              <Route path="banners" element={<AdminBanners />} />
              <Route path="reviews" element={<AdminReviews />} />
              <Route path="leads" element={<AdminLeads />} />
              <Route path="cms" element={<AdminCms />} />
              <Route path="logs" element={<AdminLogs />} />
              <Route path="roles" element={<AdminRoles />} />
              <Route path="settings" element={<AdminSettings />} />
              <Route path="franchise" element={<AdminFranchise />} />
              <Route path="educator-plans" element={<AdminEducatorPlans />} />
              <Route path="internships" element={<AdminInternships />} />
              <Route path="test-series" element={<AdminTestSeries />} />
            </Route>
          </Routes>
        </BrowserRouter>
      </CartProvider>
    </AuthProvider>
  );
}

export default App;
